import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Star, Loader2, Import, Check } from "lucide-react";
import { useItunesSearch } from "@/hooks/useItunesSearch";
import { AppStoreSearchResult, AppPlatform } from "@/types/application";
import { cn } from "@/lib/utils";

const PLATFORMS: { value: AppPlatform; label: string }[] = [
  { value: 'ios', label: 'iOS' },
  { value: 'ipados', label: 'iPadOS' },
  { value: 'macos', label: 'macOS' },
  { value: 'tvos', label: 'tvOS' },
];

const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'JP', label: 'Japan' },
  { value: 'CN', label: 'China' },
  { value: 'IN', label: 'India' },
  { value: 'BR', label: 'Brazil' },
];

interface AppStoreSearchProps {
  onSelectApp: (app: AppStoreSearchResult) => void;
  selectedAppId?: string;
  initialSearchTerm?: string;
}

export const AppStoreSearch = ({ onSelectApp, selectedAppId, initialSearchTerm = "" }: AppStoreSearchProps) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [platform, setPlatform] = useState<AppPlatform>("ios");
  const [country, setCountry] = useState("US");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  
  const { results, loading, search, lastSearchTerm } = useItunesSearch();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Trigger search when debounced term or filters change
  useEffect(() => {
    if (debouncedTerm.trim().length >= 2) {
      search({ term: debouncedTerm, country, platform });
    }
  }, [debouncedTerm, country, platform, search]);

  const handleSelectApp = useCallback((app: AppStoreSearchResult) => {
    onSelectApp({ ...app, platform }); // Ensure platform is set correctly
  }, [onSelectApp, platform]);

  return (
    <div className="space-y-4">
      {/* Search filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            className="pl-9" 
            placeholder="Search App Store..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Select value={platform} onValueChange={(v) => setPlatform(v as AppPlatform)}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            {PLATFORMS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={country} onValueChange={setCountry}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            {COUNTRIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Searching App Store...</span>
        </div>
      ) : results.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Application</TableHead>
                <TableHead>Developer</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {results.map((app) => (
                <TableRow 
                  key={app.appStoreId}
                  className={cn(
                    "cursor-pointer transition-colors",
                    selectedAppId === app.appStoreId && "bg-muted"
                  )}
                  onClick={() => handleSelectApp(app)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 border">
                        <img 
                          src={app.icon} 
                          alt={app.name} 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder.svg';
                          }}
                        />
                      </div>
                      <div>
                        <div className="font-medium">{app.name}</div>
                        <div className="text-xs text-muted-foreground">{app.bundleId}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {app.developer}
                  </TableCell>
                  <TableCell>
                    <Badge variant={app.price === 0 ? "secondary" : "outline"}>
                      {app.formattedPrice}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {app.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-yellow-400 text-warning" />
                        <span className="text-sm">{app.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant={selectedAppId === app.appStoreId ? "default" : "outline"} 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectApp(app);
                      }}
                    >
                      {selectedAppId === app.appStoreId ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Selected
                        </>
                      ) : (
                        <>
                          <Import className="h-4 w-4 mr-1" />
                          Import
                        </>
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : lastSearchTerm && !loading ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>No apps found for "{lastSearchTerm}"</p>
          <p className="text-sm mt-1">Try a different search term or change the platform/country</p>
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground border rounded-lg bg-muted/30">
          <Search className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p>Search the App Store</p>
          <p className="text-sm mt-1">Enter at least 2 characters to search</p>
        </div>
      )}
    </div>
  );
};
